import { AgencyDetailsInput, MdtPurchaseWindow } from '@clipr/lib';

import { AgencySignupFormModel, mdtPurchaseWindowOptions } from '../../pages/agencySignupPage/agencySignupSchema';

type AgencyProgramDetails = {
  reseller: string;
  resellerPocName?: string;
  resellerPocEmail?: string;
  mdtPurchaseWindow: MdtPurchaseWindow;
  mdtManufacturer: string;
  mdtModel: string;
  bwcPlatform: string;
  rmsPlatform: string;
};

/** The same fields the signup form collects, so the two cannot drift apart. */
export type AgencyDetailsForm = Pick<
  AgencySignupFormModel,
  | 'agencyName'
  | 'swornOfficersCount'
  | 'reseller'
  | 'resellerPocName'
  | 'resellerPocEmail'
  | 'mdtPurchaseWindow'
  | 'mdtManufacturer'
  | 'mdtModel'
  | 'bwcPlatform'
  | 'rmsPlatform'
> & {
  /**
   * Row version the editor was opened on, sent back so the server can reject a save built on
   * values someone else has since replaced. Captured at open, never at submit -- the underlying
   * template can be patched by other mutations while the modal sits there.
   */
  expectedVersion?: number;
};

type AgencyDetailsSource = {
  agencyName?: string | null;
  swornOfficersCount?: number | null;
  program?: unknown;
  version?: number | null;
};

export function createAgencyDetailsForm(source: AgencyDetailsSource | null | undefined): AgencyDetailsForm {
  const program = parseAgencyProgram(source?.program);
  return {
    expectedVersion: source?.version ?? undefined,
    agencyName: source?.agencyName ?? '',
    swornOfficersCount: source?.swornOfficersCount ? String(source.swornOfficersCount) : '',
    reseller: program.reseller,
    resellerPocName: program.resellerPocName ?? '',
    resellerPocEmail: program.resellerPocEmail ?? '',
    mdtPurchaseWindow: program.mdtPurchaseWindow,
    mdtManufacturer: program.mdtManufacturer,
    mdtModel: program.mdtModel,
    bwcPlatform: program.bwcPlatform,
    rmsPlatform: program.rmsPlatform,
  };
}

export function toAgencyDetailsInput(form: AgencyDetailsForm): [AgencyDetailsInput | null, string | null] {
  const swornOfficersCount = Number(form.swornOfficersCount);
  const agencyName = form.agencyName.trim();
  const reseller = form.reseller.trim();
  const mdtManufacturer = form.mdtManufacturer.trim();
  const mdtModel = form.mdtModel.trim();
  const bwcPlatform = form.bwcPlatform.trim();
  const rmsPlatform = form.rmsPlatform.trim();
  const resellerPocEmail = form.resellerPocEmail.trim();

  if (!agencyName)
    return [null, 'Agency name is required'];

  if (!Number.isInteger(swornOfficersCount) || swornOfficersCount < 1 || swornOfficersCount > 100)
    return [null, 'Sworn officers count must be between 1 and 100'];

  if (resellerPocEmail && !/^\S+@\S+\.\S+$/.test(resellerPocEmail))
    return [null, 'Reseller point of contact email is invalid'];

  // The program fields are optional: agencies provisioned outside the signup form carry
  // placeholders for them, and an unrelated correction should not be blocked on inventing one.
  return [{
    expectedVersion: form.expectedVersion,
    agencyName,
    swornOfficersCount,
    reseller: reseller || undefined,
    resellerPocName: form.resellerPocName.trim() || undefined,
    resellerPocEmail: resellerPocEmail ? resellerPocEmail.toLowerCase() : undefined,
    mdtPurchaseWindow: form.mdtPurchaseWindow,
    mdtManufacturer: mdtManufacturer || undefined,
    mdtModel: mdtModel || undefined,
    bwcPlatform: bwcPlatform || undefined,
    rmsPlatform: rmsPlatform || undefined,
  }, null];
}

export function parseAgencyProgram(value: unknown): AgencyProgramDetails {
  const parsed = parseJson(value);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return emptyProgram();
  }

  const record = parsed as Record<string, unknown>;
  return {
    reseller: stringValue(record.reseller),
    resellerPocName: optionalStringValue(record.resellerPocName),
    resellerPocEmail: optionalStringValue(record.resellerPocEmail),
    mdtPurchaseWindow: isMdtPurchaseWindow(record.mdtPurchaseWindow)
      ? record.mdtPurchaseWindow
      : MdtPurchaseWindow.NotSure,
    mdtManufacturer: stringValue(record.mdtManufacturer),
    mdtModel: stringValue(record.mdtModel),
    bwcPlatform: stringValue(record.bwcPlatform),
    rmsPlatform: stringValue(record.rmsPlatform),
  };
}

export function formatMdtPurchaseWindow(value: MdtPurchaseWindow): string {
  return mdtPurchaseWindowOptions.find(option => option.value === value)?.label ?? 'Not sure';
}

function emptyProgram(): AgencyProgramDetails {
  return {
    reseller: '',
    mdtPurchaseWindow: MdtPurchaseWindow.NotSure,
    mdtManufacturer: '',
    mdtModel: '',
    bwcPlatform: '',
    rmsPlatform: '',
  };
}

/** AWSJSON reaches the client as a string on some paths and as an object on others. */
export function parseJson(value: unknown): unknown {
  if (typeof value !== 'string')
    return value;

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function optionalStringValue(value: unknown): string | undefined {
  const str = stringValue(value).trim();
  return str || undefined;
}

function isMdtPurchaseWindow(value: unknown): value is MdtPurchaseWindow {
  return value === MdtPurchaseWindow.Last_3Months ||
    value === MdtPurchaseWindow.Next_3Months ||
    value === MdtPurchaseWindow.NotSure;
}
